import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import {useFullscreen} from '@/shared/ui/hooks/use-fullscreen';

type ImmersiveChromeValue = {
  readonly isImmersive: boolean;
  readonly setImmersive: Dispatch<SetStateAction<boolean>>;
};

const ImmersiveChromeContext = createContext<ImmersiveChromeValue | null>(null);

type ImmersiveChromeProviderProps = {
  readonly children: ReactNode;
  readonly defaultImmersive?: boolean;
};

/**
 * Lets a page request a chrome-less shell. Leaving immersive mode also releases any
 * document fullscreen the page requested, so features only request and toggle it.
 */
export const ImmersiveChromeProvider = ({
  children,
  defaultImmersive = false,
}: ImmersiveChromeProviderProps) => {
  const [isImmersive, setImmersive] = useState(defaultImmersive);
  const {exit: exitFullscreen} = useFullscreen();
  const value = useMemo(() => ({isImmersive, setImmersive}), [isImmersive]);

  useEffect(() => {
    if (!isImmersive) {
      void exitFullscreen();
    }
  }, [isImmersive, exitFullscreen]);

  return (
    <ImmersiveChromeContext.Provider value={value}>{children}</ImmersiveChromeContext.Provider>
  );
};

export const useImmersiveChrome = (): ImmersiveChromeValue => {
  const value = useContext(ImmersiveChromeContext);

  if (value === null) {
    throw new Error('useImmersiveChrome must be used within ImmersiveChromeProvider.');
  }

  return value;
};
